import React from 'react';
import classnames from 'classnames';
import { buildBemClassName } from '../modules/util';
import './HeaderNav.scss';
import Player, { AutoConfig, PlaybackStateChangeHandler } from './Player';
import RepeatControl from './RepeatControl';
import { Measure } from '../modules/vex';
import { NoteTriggerHandler, PlaybackState } from '../modules/tone';
import { useTranslation } from 'react-i18next';

const buildClassName = buildBemClassName('c-rr-header-nav');

export interface HeaderNavProps {
  measures: Measure[];
  playbackState: PlaybackState;
  metronomeOn: boolean;
  repeat: number;
  repeatOptions: number[];
  autoMode: boolean;
  autoConfig: AutoConfig | null;
  onPlaybackStateChange: PlaybackStateChangeHandler;
  onNoteTrigger: NoteTriggerHandler;
  onMetronomeClickTrigger: NoteTriggerHandler;
  onRandomizeButtonClick: () => void;
  onMetronomeButtonClick: () => void;
  onRepeatChange: (repeat: number) => void;
  onAutoModeButtonClick: () => void;
}

const HeaderNav = ({
  measures,
  playbackState,
  metronomeOn,
  repeat,
  repeatOptions,
  autoMode,
  autoConfig,
  onPlaybackStateChange,
  onNoteTrigger,
  onMetronomeClickTrigger,
  onRandomizeButtonClick,
  onMetronomeButtonClick,
  onRepeatChange,
  onAutoModeButtonClick,
}: HeaderNavProps) => {
  const { t } = useTranslation();

  return (
    <nav className={buildClassName()()}>
      <ul className={buildClassName('list')()}>
        <li className={buildClassName('list-item')()}>
          <button
            type="button"
            className={classnames(
              buildClassName('item-link')(),
              'c-rr-button',
              'c-rr-button--dark'
            )}
            onClick={onRandomizeButtonClick}
            disabled={playbackState === PlaybackState.PLAYING || metronomeOn}
          >
            {t('newRhythm')}
          </button>
        </li>
        <li className={buildClassName('list-item')()}>
          <Player
            measures={measures}
            playbackState={playbackState}
            metronomeOn={metronomeOn}
            repeat={repeat}
            autoConfig={autoConfig}
            className={buildClassName('item-link')()}
            onPlaybackStateChange={onPlaybackStateChange}
            onNoteTrigger={onNoteTrigger}
            onMetronomeClickTrigger={onMetronomeClickTrigger}
          />
        </li>
        <li className={buildClassName('list-item')()}>
          <RepeatControl
            repeat={repeat}
            options={repeatOptions}
            className={buildClassName('item-link')()}
            disabled={playbackState === PlaybackState.PLAYING || metronomeOn}
            onRepeatChange={onRepeatChange}
          />
        </li>
        <li className={buildClassName('list-item')()}>
          <button
            type="button"
            className={classnames(
              buildClassName('item-link')(),
              'c-rr-button',
              'c-rr-button--dark',
              {
                [buildClassName('item-link')('active')]: autoMode,
              }
            )}
            onClick={onAutoModeButtonClick}
            disabled={metronomeOn}
            aria-pressed={autoMode}
          >
            {t('auto')}
            {autoMode ? ' ✓' : ''}
          </button>
        </li>
        <li className={buildClassName('list-item')()}>
          <button
            type="button"
            className={classnames(
              buildClassName('item-link')(),
              'c-rr-button',
              'c-rr-button--dark'
            )}
            onClick={onMetronomeButtonClick}
            disabled={playbackState === PlaybackState.PLAYING}
          >
            {metronomeOn ? t('stopMetronome') : t('startMetronome')}
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default HeaderNav;
